<template>
  <span>
    <span class="btn btn-light"
            @click="showCreateSModal"
            v-if="isGranted(permissions.RISK_SCENARIO_CREATE)"
    >
      <font-awesome-icon icon="plus-circle"/> {{ $t('risk_scenarios.create_button') }}
    </span>

    <!-- Create System modal template -->
    <b-modal ref="create-system" hide-footer @hide="hideCreateSModal">
      <div class="d-block text-center">
        <h4 qid="update-tp-title">
          {{ $t('risk_scenarios.create.title') }}
        </h4>
      </div>
      <b-alert v-if="error.length"
               show
               variant="danger"
               qid="create-p-error"
      >
        {{ error }}
      </b-alert>
      <b-form @submit="formSubmit" qid="create-p-form">
        <div class="form-container">
          <b-form-group>
            <label qid="create-p-name-label">
              {{ $t('risk_scenarios.create.name_label') }}
            </label>
            <b-form-input
              qid="create-p-form-name"
              type="text"
              v-model="pForm.name"
              :maxLength="255"
              required
              :placeholder="$t('risk_scenarios.create.name_placeholder')"
            ></b-form-input>
          </b-form-group>
          <b-form-group>
            <label qid="create-p-description-label">
              {{ $t('risk_scenarios.create.description_label') }}
            </label>
            <b-form-textarea
              qid="create-p-form-description"
              v-model="pForm.description"
              rows="6"
              max-rows="6"
              :placeholder="$t('risk_scenarios.create.description_placeholder')"
            ></b-form-textarea>
          </b-form-group>

          <b-form-group>
            <label qid="create-do-tags-label">
              {{ $t('processes.create.tags_label') }}
            </label>
            <multiselect
                    v-model="selectedTags"
                    :options="tags"
                    :multiple="true"
                    :select-label="$t('system.dropdown_select_label')"
                    :selected-label="$t('system.dropdown_selected_label')"
                    :deselect-label="$t('system.dropdown_deselect_label')"
                    :taggable="true"
                    track-by="name_translated"
                    :placeholder="$t('processes.create.tags_placeholder')"
                    label="name_translated"
                    qid="create-do-tags-select"
                    @search-change="loadTags"
                    @tag="addTag"
            ></multiselect>
          </b-form-group>

          <b-form-group>
            <label qid="create-p-pg-label">
              {{ $t('risk_scenarios.create.risk_category_label') }}
            </label>
            <multiselect
                v-model="riskCategory"
                :options="riskCategories"
                :select-label="$t('system.dropdown_select_label')"
                :selected-label="$t('system.dropdown_selected_label')"
                :deselect-label="$t('system.dropdown_deselect_label')"
                :placeholder="$t('risk_scenarios.create.risk_category_placeholder')"
                label="name"
                @search-change="loadRiskCategories"
                qid="create-p-form-pg"
            ></multiselect>
          </b-form-group>
          <b-form-group>
            <label qid="create-tp-type-label">
              {{ $t('table.scope') }}
            </label>
            <multiselect
                v-model="selectedTypes"
                :options="types"
                :multiple="true"
                track-by="value"
                :taggable="true"
                :select-label="$t('system.dropdown_select_label')"
                :selected-label="$t('system.dropdown_selected_label')"
                :deselect-label="$t('system.dropdown_deselect_label')"
                :placeholder="$t('table.scope')"
                label="name"
                qid="create-tp-form-type"
            >
              <template slot="noResult" slot-scope="props">
                {{ $t('maintenance.data_subjects.create.subjects_no_results') }}
              </template>
            </multiselect>
          </b-form-group>
          <b-form-group>
            <label qid="create-risk-scenario-risk-type-label">
              {{ $t('risk_scenarios.create.risk_type_label') }}
            </label>
            <multiselect
                v-model="selectedRiskTypes"
                :options="riskTypes"
                :multiple="true"
                track-by="value"
                :taggable="false"
                :select-label="$t('system.dropdown_select_label')"
                :selected-label="$t('system.dropdown_selected_label')"
                :deselect-label="$t('system.dropdown_deselect_label')"
                :placeholder="$t('risk_scenarios.create.risk_type_placeholder')"
                label="name"
                qid="create-tp-form-type"
            ></multiselect>
          </b-form-group>
          <b-form-group>
            <label qid="create-p-users-label">
              {{ $t('risk_scenarios.create.owners_label') }}
            </label>
            <multiselect
                v-model="selectedUsers"
                :options="users"
                :multiple="true"
                :taggable="false"
                track-by="slug"
                :placeholder="$t('risk_scenarios.create.owners_placeholder')"
                label="name"
                :select-label="$t('system.dropdown_select_label')"
                :selected-label="$t('system.dropdown_selected_label')"
                :deselect-label="$t('system.dropdown_deselect_label')"
                @remove="removeUserFromDropdown"
                @search-change="loadUsers"
                qid="create-p-users-select"
            >
              <template slot="tag" slot-scope="props">
                <span></span>
              </template>
              <template slot="option" slot-scope="props">
                <Avatar :object="props.option" size="xs"></Avatar>
                <span class="ml-2">
                  {{ props.option.name }}
                </span>
              </template>
            </multiselect>
            <div class="form-control auto-height mt-1" v-if="selectedUsers && selectedUsers.length">
              <div v-for="(user, index) in selectedUsers" class="mt-1 mb-1">
                <div class="row">
                  <div class="col-sm-1">
                    <font-awesome-icon
                      icon="star"
                      :class="{'color-yellow': user.owner, 'text-light': !user.owner}"
                      class="cursor-pointer"
                      @click="setOwner(index)"
                    />
                  </div>
                  <div class="col-sm-10">
                    <Avatar :object="user" size="xs" :noOwner="true"></Avatar>
                    <span class="ml-2">{{ user.name }}</span>
                  </div>
                  <div class="col-sm-1">
                    <font-awesome-icon
                      icon="times"
                      class="cursor-pointer"
                      @click="removeUser(index)"
                    />
                  </div>
                </div>
              </div>
            </div>
            <span class="text-light">
              * {{ $t('risk_scenarios.create.owners_tip') }}
            </span>
          </b-form-group>
        </div>
        <div class="text-right mr-4">
          <b-button class="mt-3"
                    variant="secondary"
                    @click="hideCreateSModal"
                    qid="create-p-cancel-button"
          >
            {{ $t('risk_scenarios.create.cancel') }}
          </b-button>
          <b-button type="submit"
                    class="mt-3 ml-3"
                    variant="success"
                    :disabled="buttonDisabled"
                    qid="create-p-submit-button"
          >
            <span v-if="buttonDisabled" class="mr-1">
              <font-awesome-icon icon="spinner" size="sm" class="fa-spin"/>
            </span>
            {{ $t('risk_scenarios.create.submit') }}
          </b-button>
        </div>
      </b-form>
    </b-modal>
  </span>

</template>

<script>
  import piincHttp from '@/util/configureAxios.js'
  import Avatar from '@/components/Avatar.vue';
  import Datepicker from 'vuejs-datepicker';

  export default {
    name: 'RiskScenarioCreateForm',
    data: function() {
      return {
        pForm: {
          name: '',
          description: '',
          types: [],
          users: [],
          tags: [],
          client:'',
        },
        error: '',
        users: [],
        selectedUsers: [],
        processGroups: [],
        processGroup: [],
        buttonDisabled: false,
        types: [
          { name: this.$t('risk_scenarios.type.process'), value: 'process' },
          { name: this.$t('risk_scenarios.type.activity'), value: 'activity' },
          { name: this.$t('risk_scenarios.type.system'), value: 'system' },
          { name: this.$t('risk_scenarios.type.third_party'), value: 'third_party' }
        ],
        riskTypes: [
          { name: this.$t('risk_scenarios.risk_type.business_risk'), value: 'business_risk' },
          { name: this.$t('risk_scenarios.risk_type.privacy_risk'), value: 'privacy_risk' }
        ],
        selectedTypes: [],
        selectedRiskTypes: [],
        riskCategory: [],
        riskCategories: [],
        selectedTags: [],
        tags: [],
        newTags: [],
      }
    },
    components: {
      Datepicker,
      Avatar
    },
    props: {

    },
    computed: {
      getClient() {
        return this.$store.state.client
      },
      getFiles() {
        return this.selectedFiles
      }
    },
    watch: {
      // Watch when client changes in store and trigger api reload
      getClient: function () {
        this.loadTags()
      }
    },
    methods: {
      addTag(newTag) {
        const tag = {
          name: newTag,
          translatable: newTag,
          name_translated: newTag
        }
        this.selectedTags.push(tag)
        this.newTags.push(tag)
      },
      removeUser(index) {
        let selectedItem = this.selectedUsers[index]
        selectedItem.owner = false;
        this.$set(this.selectedUsers, index, selectedItem)
        // Remove item from selected users array
        this.selectedUsers.splice(index, 1);
      },
      removeUserFromDropdown(item) {
        item.owner = false;
      },
      setOwner(index) {
        let selectedItem = this.selectedUsers[index]
        _.forEach(this.selectedUsers, function (item) {
          item.owner = false;
        })
        selectedItem.owner = true;
        this.$set(this.selectedUsers, index, selectedItem)
      },
      formSubmit(evt) {
        this.buttonDisabled = true;
        let formData = new FormData();
        let files = document.querySelector('#file');
        let selectedOwner = false;

        // Keep the reference in a variable
        // to use it inside function called when promise returned
        let self = this;
        evt.preventDefault();

        // Append list of users to FormData
        if (this.selectedUsers && this.selectedUsers.length) {
          for (var i = 0; i < this.selectedUsers.length; i++) {
            formData.append("users[]", this.selectedUsers[i].id);
            if (this.selectedUsers[i].owner) {
              selectedOwner = true;
              formData.append("owner", this.selectedUsers[i].id);
            }
          }
        } else {
          selectedOwner = true;
        }

        if (this.riskCategory && this.riskCategory.name) {
          formData.append("risk_category_id", this.riskCategory.id);
        }

        // Append list of types
        if (this.selectedTypes && this.selectedTypes.length) {
          for (var i = 0; i < this.selectedTypes.length; i++) {
            formData.append("types[]", this.selectedTypes[i].value);
          }
        }

        // Append list of risk types
        if (this.selectedRiskTypes && this.selectedRiskTypes.length) {
          for (var i = 0; i < this.selectedRiskTypes.length; i++) {
            formData.append("risk_types[]", this.selectedRiskTypes[i].value);
          }
        }

        if (this.selectedTags && this.selectedTags.length) {
          for (var i = 0; i < this.selectedTags.length; i++) {
            formData.append("tags[]", this.selectedTags[i].name_translated);
          }
        }

        formData.append("name", this.pForm.name);
        formData.append("description", this.pForm.description);
        formData.append("client", this.getClient.slug);

        if (selectedOwner && this.selectedTypes && this.selectedTypes.length && this.selectedRiskTypes && this.selectedRiskTypes.length) {
          piincHttp.post('/risk-scenarios', formData, {
            headers: {
              'Content-Type': 'multipart/form-data'
            }
          }).then(function(response) {
            self.$toastr('success', self.$t('risk_scenarios.create.success'))
            self.buttonDisabled = false;
            self.hideCreateSModal()
          }, function(error) {
            if (error.status !== 200) {
              self.buttonDisabled = false;
              self.error = error.data.message;
              // Print whole array of actual errors beside message
              self.error = self.error + '\n\n' + JSON.stringify(error.data.errors);
            }
          })
        } else {
          this.error = ''
          if (!selectedOwner) {
            this.error += this.$t('system.owners_error_message')
          }
          if (this.selectedTypes && !this.selectedTypes.length) {
            this.error +=  ' ' + this.$t('system.type_error_message')
          }
          if (this.selectedRiskTypes && !this.selectedRiskTypes.length) {
            this.error +=  ' ' + this.$t('system.risk_type_error_message')
          }
          this.buttonDisabled = false;
        }
      },
      loadUsers(query) {
        let self = this;
        piincHttp.get('users', { params:
          {
            client: this.getClient.id,
            keyword: query ? query : undefined,
            per_page: 50
          }
        }).then(function(response) {
          self.users = response.data.items
          if (self.users && !self.users.length && !query) {
            self.users.push({name: self.$t('system.no_results'), $isDisabled: true})
          }
        }, function() {});
      },
      loadRiskCategories(query) {
        let self = this;
        piincHttp.get('risk-categories', { params:
          {
            client: this.getClient.slug,
            keyword: query ? query : undefined,
            per_page: 100
          }
        }).then(function(response) {
          self.riskCategories = response.data.items
          if (self.riskCategories && !self.riskCategories.length && !query) {
            self.riskCategories.push({name: self.$t('system.no_results'), $isDisabled: true})
          }
        }, function() {});
      },
      showCreateSModal() {
        this.loadUsers()
        this.loadRiskCategories()
        this.error = ''
        this.$refs['create-system'].show()
      },
      loadTags(query) {
        let self = this;
        piincHttp.get('tags', { params:
                  {
                    client: this.getClient.slug,
                    keyword: query ? query : undefined
                  }
        }).then(function(response) {
          self.tags = response.data
          for (var i = 0; i < self.newTags.length; i++) {
            self.tags.push(self.newTags[i]);
          }
          if (self.tags && !self.tags.length && !query) {
            self.tags.push({name_translated: self.$t('system.no_results'), $isDisabled: true})
          }
        }, function() {});
      },
      hideCreateSModal() {
        this.pForm = {
          name: '',
          description: '',
          types: [],
          users: [],
          tags: [],
          client:'',
        }
        this.riskCategory = []
        this.selectedUsers = []
        this.$refs['create-system'].hide()
      }
    },
    created () {
      if (this.getClient && this.getClient.slug) {
        this.loadTags()
      }
    }
  }
</script>
